import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Sun, Zap, TrendingUp, Bot, Mail, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BG = "https://images.pexels.com/photos/17762230/pexels-photo-17762230.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function formatApiError(detail) {
  if (detail == null) return "Etwas ist schiefgelaufen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, agency: authAgency, loading, checkAuth } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const agencySlug = params.get("agency") || "";
  const returnUrl = params.get("return_url") || "";
  const errorParam = params.get("error");
  const initialMode = params.get("mode") === "register" ? "register" : "login";

  const [portalAgency, setPortalAgency] = useState(null);
  const [mode, setMode] = useState(initialMode); // "login" | "register" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(() => {
    if (!errorParam) return "";
    const map = {
      token_exchange: "Google-Login fehlgeschlagen (Token).",
      userinfo: "Google-Login fehlgeschlagen (Profil).",
      email_unverified: "E-Mail bei Google nicht bestätigt.",
      no_token: "Google-Login: kein Token erhalten.",
      access_denied: "Login abgebrochen.",
    };
    return map[errorParam] || `Login fehlgeschlagen: ${errorParam}`;
  });
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (!agencySlug) return;
    let cancelled = false;
    axios.get(`${BACKEND}/api/public/agencies/${encodeURIComponent(agencySlug)}`)
      .then((r) => { if (!cancelled) setPortalAgency(r.data); })
      .catch(() => { /* fallback default branding */ });
    return () => { cancelled = true; };
  }, [agencySlug]);

  useEffect(() => {
    if (loading || !user) return;
    if (authAgency && authAgency.onboarding_completed === false &&
        (user.role === "agency_admin" || user.role === "platform_admin")) {
      nav("/agency/onboarding", { replace: true });
    } else {
      nav(returnUrl || "/dashboard", { replace: true });
    }
  }, [user, authAgency, loading, nav, returnUrl]);

  const handleGoogle = () => {
    setErr("");
    if (agencySlug) {
      try { sessionStorage.setItem("pending_portal_login", agencySlug); } catch { /* noop */ }
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const r = encodeURIComponent(returnUrl || "/dashboard");
    window.location.href = `${BACKEND}/api/auth/google/login?return_to=${r}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setInfo("");
    try {
      if (mode === "register") {
        await api.post("/auth/register", { email, password, name });
        await checkAuth();
        toast.success("Konto erstellt! Bitte E-Mail-Adresse bestätigen.");
      } else if (mode === "login") {
        await api.post("/auth/login", { email, password });
        await checkAuth();
        toast.success("Willkommen zurück!");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setInfo("Falls ein Konto mit dieser E-Mail existiert, wurde ein Rücksetzungs-Link verschickt.");
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const isPortal = Boolean(portalAgency);
  const primary = portalAgency?.primary_color || "#f97316";
  const accent = portalAgency?.accent_color || "#0f172a";
  const brandName = portalAgency?.name || "revenioo.ai";
  const heroHeadline = isPortal
    ? `Willkommen im Portal von ${brandName}.`
    : "Mehr Abschlüsse aus bestehenden PV-Anfragen.";
  const heroSub = isPortal
    ? "Verwalten Sie Ihre PV-Leads, Angebote, Follow-ups und Hot Leads zentral an einem Ort."
    : "Schnellere Angebote, automatisches Nachfassen, KI-Einwandbehandlung – für PV-Betriebe, die ihre Pipeline ausschöpfen wollen.";

  return (
    <div className="min-h-screen flex" data-testid={isPortal ? "login-portal-branded" : "login-default"}>
      {/* Left: hero (unchanged from previous version) */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Solar" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: isPortal ? `${accent}D9` : "rgba(15,23,42,0.7)" }} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2" data-testid="login-brand">
            {portalAgency?.logo_url ? (
              <img src={portalAgency.logo_url} alt={portalAgency.name}
                   className="h-10 max-w-[180px] object-contain bg-white/10 rounded-lg p-1.5 border border-white/15"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isPortal ? primary : "#f97316" }}>
                <Sun className="w-6 h-6" strokeWidth={2.5} />
              </div>
            )}
            <span className="font-extrabold text-xl" style={{ fontFamily: "Manrope, sans-serif" }}>{brandName}</span>
          </div>
          <div className="space-y-6 max-w-md">
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
              {heroHeadline}
            </h1>
            <p className="text-slate-200 text-lg">{heroSub}</p>
            <div className="grid grid-cols-3 gap-4 pt-4">
              <Feature icon={Zap} label="Angebote in Minuten" color={primary} />
              <Feature icon={Bot} label="KI-Nachfassen" color={primary} />
              <Feature icon={TrendingUp} label="Höhere Abschlussquote" color={primary} />
            </div>
          </div>
          <p className="text-xs text-slate-300">
            {isPortal ? `Powered by revenioo.ai · © 2026 ${brandName}` : "© 2026 revenioo.ai – Made for Solarteure"}
          </p>
        </div>
      </div>

      {/* Right: auth panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            {portalAgency?.logo_url ? (
              <img src={portalAgency.logo_url} alt="" className="h-10 max-w-[160px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: isPortal ? primary : "#f97316" }}>
                <Sun className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
            )}
            <span className="font-extrabold text-xl" style={{ fontFamily: "Manrope, sans-serif" }}>{brandName}</span>
          </div>

          {/* Mode tabs */}
          {mode !== "forgot" && (
            <div className="flex bg-slate-100 rounded-lg p-1 mb-6" role="tablist">
              <button
                type="button"
                role="tab"
                data-testid="auth-tab-login"
                onClick={() => { setMode("login"); setErr(""); setInfo(""); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold transition ${mode === "login" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >Anmelden</button>
              <button
                type="button"
                role="tab"
                data-testid="auth-tab-register"
                onClick={() => { setMode("register"); setErr(""); setInfo(""); }}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold transition ${mode === "register" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >Registrieren</button>
            </div>
          )}

          <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            {mode === "register" ? "Konto erstellen" : mode === "forgot" ? "Passwort zurücksetzen" : "Willkommen zurück"}
          </h2>
          <p className="text-slate-600 mb-6 text-sm">
            {mode === "register" && "30 Sekunden zum Start — kein Kreditkarten-Eintrag nötig."}
            {mode === "login" && "Melden Sie sich an, um Leads, Angebote und Nachfass-Sequenzen zu verwalten."}
            {mode === "forgot" && "Wir senden Ihnen einen Link, um Ihr Passwort neu zu setzen."}
          </p>

          {/* Google button — only shown for login/register */}
          {mode !== "forgot" && (
            <>
              <Button
                type="button"
                data-testid="google-login-btn"
                onClick={handleGoogle}
                variant="outline"
                className="w-full h-11 text-base font-semibold border-slate-300 mb-4"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 mr-3" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
                </svg>
                {mode === "register" ? "Mit Google registrieren" : "Mit Google anmelden"}
              </Button>

              <div className="flex items-center my-4">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="px-3 text-xs text-slate-400 uppercase tracking-wider">oder</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid={`auth-form-${mode}`}>
            {mode === "register" && (
              <div>
                <Label htmlFor="auth-name" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Name</Label>
                <div className="relative mt-1">
                  <UserIcon className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="auth-name" data-testid="auth-input-name"
                    type="text" required minLength={1} maxLength={120}
                    value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Max Mustermann"
                    className="pl-10 h-11"
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="auth-email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">E-Mail</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  id="auth-email" data-testid="auth-input-email"
                  type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  className="pl-10 h-11"
                />
              </div>
            </div>

            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="auth-password" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Passwort</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="auth-forgot-link"
                            onClick={() => { setMode("forgot"); setErr(""); setInfo(""); }}
                            className="text-xs text-orange-600 hover:text-orange-700 hover:underline">
                      Vergessen?
                    </button>
                  )}
                </div>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                  <Input
                    id="auth-password" data-testid="auth-input-password"
                    type="password" required minLength={8} maxLength={128}
                    autoComplete={mode === "register" ? "new-password" : "current-password"}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === "register" ? "min. 8 Zeichen" : "Ihr Passwort"}
                    className="pl-10 h-11"
                  />
                </div>
                {mode === "register" && (
                  <p className="mt-1 text-[11px] text-slate-500">Mindestens 8 Zeichen. Tipp: Mix aus Buchstaben, Zahlen, Sonderzeichen.</p>
                )}
              </div>
            )}

            {err && (
              <div data-testid="auth-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
                {err}
              </div>
            )}
            {info && (
              <div data-testid="auth-info" className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                {info}
              </div>
            )}

            <Button
              type="submit"
              data-testid={`auth-submit-${mode}`}
              disabled={busy}
              className="w-full h-11 text-white text-base font-semibold"
              style={{ background: isPortal ? accent : "#0f172a" }}
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === "register" ? "Konto erstellen" : mode === "login" ? "Anmelden" : "Rücksetzungs-Link senden"}
            </Button>

            {mode === "forgot" && (
              <button type="button" data-testid="auth-back-to-login"
                      onClick={() => { setMode("login"); setErr(""); setInfo(""); }}
                      className="w-full text-sm text-slate-600 hover:text-slate-900 underline">
                ← Zurück zur Anmeldung
              </button>
            )}
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            Mit der Anmeldung akzeptieren Sie die <Link to="/agb" className="underline hover:text-slate-900" data-testid="login-terms-link">Nutzungsbedingungen</Link>. Wir nutzen Ihre Daten ausschließlich zur Bereitstellung der App — Details unter <Link to="/datenschutz" className="underline hover:text-slate-900" data-testid="login-privacy-link">Datenschutz</Link>.
          </p>
          {isPortal && (
            <p className="mt-3 text-center text-[11px] text-slate-400" data-testid="login-powered-by">
              Powered by revenioo.ai
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-slate-400" data-testid="login-legal-links">
            <Link to="/impressum" className="hover:text-slate-700" data-testid="login-link-imprint">Impressum</Link>
            <span>·</span>
            <Link to="/datenschutz" className="hover:text-slate-700" data-testid="login-link-privacy">Datenschutz</Link>
            <span>·</span>
            <Link to="/agb" className="hover:text-slate-700" data-testid="login-link-terms">AGB</Link>
            <span>·</span>
            <Link to="/cookies" className="hover:text-slate-700" data-testid="login-link-cookies">Cookies</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const Feature = ({ icon: Icon, label, color }) => (
  <div className="flex flex-col items-start gap-2">
    <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
      <Icon className="w-4 h-4" style={{ color: color || "#fb923c" }} />
    </div>
    <span className="text-xs text-slate-200">{label}</span>
  </div>
);
